import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert, Button, Checkbox, Form, Input, InputNumber, Select, Space,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import ModalWrapper from './ModalWrapper';
import csrfHeaders from '../../utils/csrfHeaders';
import { captureAnalytics } from '../../actions/preload';

// NextGen caps a task subject at 50 characters and Medsender prefixes "Medsender: ".
export const SUBJECT_MAX_LENGTH = 39;
const FIELD_TYPES = [
  { value: 'select', label: 'Pick list' },
  { value: 'provider_search', label: 'Referring provider search' },
  { value: 'text', label: 'Free text' },
];
const BASE = '/api/v1/nextgen_task_templates';

// Field keys are derived from the label so admins never type one; a saved
// field keeps its key so values already captured stay addressable.
export const fieldKey = (label) => {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return (/^[a-z]/.test(slug) ? slug : `field_${slug}`).slice(0, 50).replace(/_+$/, '');
};

const ownerKey = (ownerType, ownerId) => `${ownerType}:${ownerId}`;
const optionOwnerKey = (option) => ownerKey(option.owner_type, option.owner_id);
// New templates are shared with the whole organization unless narrowed.
const defaultOwner = (ownerOptions) => {
  const organization = ownerOptions.find((option) => option.owner_type === 'Organization');
  return organization ? optionOwnerKey(organization) : undefined;
};
const renderOwnerOption = (option) => (
  <Select.Option key={optionOwnerKey(option)} value={optionOwnerKey(option)}>{option.label}</Select.Option>
);
// A saved value NextGen no longer lists stays selectable, shown by its saved label.
const withSaved = (list, value, label) => (
  value && !list.some((option) => option.value === value) ? [{ value, label: label || value }, ...list] : list
);
const errorMessage = (error, fallback) => error.response?.data?.error || fallback;
const loadOptions = async (credentialId, source) => {
  const response = await axios.get('/api/v1/emr_options', {
    params: { emr_integration_credential_id: credentialId, source },
  });
  return response.data.options;
};

const AddNextgenTaskTemplate = ({
  credentialId, ownerOptions, template, onClose, onSaved,
}) => {
  const [form, setForm] = useState(() => ({
    name: template.name || '',
    owner: template.owner_type ? ownerKey(template.owner_type, template.owner_id) : defaultOwner(ownerOptions),
    documentTypeIds: (template.document_types || []).map((type) => type.id),
    categoryId: template.category_id,
    categoryLabel: template.category_label,
    priority: template.priority,
    priorityLabel: template.priority_label,
    subject: template.subject || '',
    dueInDays: template.due_in_days ?? null,
    // `uid` identifies a field row while it is edited (labels, and so keys, can
    // change); it is never sent.
    fields: (template.fields || []).map((field, index) => ({ ...field, uid: index + 1 })),
  }));
  const nextUidRef = useRef((template.fields || []).length + 1);
  const [options, setOptions] = useState({ documentTypes: [], categories: [], priorities: [] });
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  // A rejected save describes the values that were sent, so any edit clears it.
  const update = (changes) => {
    setSaveError('');
    setForm((previous) => ({ ...previous, ...changes }));
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${BASE}/document_types`, { params: { emr_integration_credential_id: credentialId } }),
      loadOptions(credentialId, 'nextgen/task_categories'),
      loadOptions(credentialId, 'nextgen/task_priorities'),
    ]).then(([documentTypes, categories, priorities]) => {
      if (!mounted) return;
      setOptions({ documentTypes: documentTypes.data.document_types, categories, priorities });
      // New templates start on NextGen's default priority (Normal), like the Send to Chart task section.
      const defaultPriority = priorities.find((option) => option.metadata?.is_default);
      if (defaultPriority) {
        setForm((previous) => (previous.priority ? previous : {
          ...previous, priority: defaultPriority.value, priorityLabel: defaultPriority.label,
        }));
      }
    }).catch((err) => {
      if (mounted) setLoadError(errorMessage(err, 'Could not load NextGen options. Please try again.'));
    });
    return () => { mounted = false; };
  }, [credentialId]);

  // Saved document types that left the catalog stay selectable so they can be removed.
  const documentTypeOptions = [
    ...options.documentTypes,
    ...(template.document_types || []).flatMap((type) => (
      options.documentTypes.some((option) => option.value === type.id) ? [] : [{ value: type.id, label: type.label || type.id }]
    )),
  ];

  const updateField = (index, changes) => update({
    fields: form.fields.map((field, position) => (position === index ? { ...field, ...changes } : field)),
  });

  const payload = () => {
    const [ownerType, ownerId] = (form.owner || '').split(':');
    return {
      name: form.name.trim(),
      owner_type: ownerType || undefined,
      owner_id: ownerId || undefined,
      document_type_ids: form.documentTypeIds,
      category_id: form.categoryId || null,
      category_label: form.categoryId ? form.categoryLabel : null,
      priority: form.priority,
      priority_label: form.priorityLabel,
      subject: form.subject.trim(),
      due_in_days: form.dueInDays,
      fields: form.fields.map(({
        key, label, type, required, options: choices, default: defaultValue,
      }) => ({
        key: key || fieldKey(label || ''),
        label: (label || '').trim(),
        type,
        required: !!required,
        ...(type === 'select' ? { options: choices || [] } : {}),
        ...(type !== 'provider_search' && defaultValue ? { default: defaultValue } : {}),
      })),
      ...(template.id ? {} : { emr_integration_credential_id: credentialId }),
    };
  };

  const save = async () => {
    setSaving(true);
    setSaveError('');
    const body = { nextgen_task_template: payload() };
    try {
      const response = template.id
        ? await axios.patch(`${BASE}/${template.id}`, body, { headers: csrfHeaders() })
        : await axios.post(BASE, body, { headers: csrfHeaders() });
      captureAnalytics(template.id ? 'nextgen_task_template_updated' : 'nextgen_task_template_created', {
        owner_type: body.nextgen_task_template.owner_type,
        document_type_count: form.documentTypeIds.length,
        field_count: form.fields.length,
      });
      onSaved(response.data.nextgen_task_template);
    } catch (err) {
      setSaveError(errorMessage(err, 'Could not save the task template. Please try again.'));
      setSaving(false);
    }
  };

  const incomplete = !form.name.trim() || !form.documentTypeIds.length || !form.priority;
  // Owners with a group (departments) are listed under that group's heading.
  const ownerGroups = [...new Set(ownerOptions.flatMap((option) => (option.group ? [option.group] : [])))];

  return (
    <ModalWrapper
      wrapClassName="settings-modal-wrap"
      customContentStyle={{ width: '720px' }}
      width="720px"
      hideModal={onClose}
      action={save}
      actionName={template.id ? 'Save' : 'Add'}
      dismiss="Cancel"
      disabled={incomplete || saving}
      isLoading={saving}
      modalTitle={template.id ? 'Edit NextGen Task Template' : 'Add NextGen Task Template'}
    >
      {(saveError || loadError) && <Alert type="error" showIcon message={saveError || loadError} style={{ marginBottom: 12 }} />}
      <Form layout="vertical">
        <Form.Item label="Template name" required>
          <Input
            aria-label="Template name"
            value={form.name}
            maxLength={100}
            onChange={(e) => update({ name: e.target.value })}
          />
        </Form.Item>
        <Form.Item label="Available to">
          <Select aria-label="Available to" value={form.owner} onChange={(owner) => update({ owner })}>
            {ownerOptions.filter((option) => !option.group).map(renderOwnerOption)}
            {ownerGroups.map((group) => (
              <Select.OptGroup key={group} label={group}>
                {ownerOptions.filter((option) => option.group === group).map(renderOwnerOption)}
              </Select.OptGroup>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="NextGen document types" required extra="The template loads when one of these is picked in Send to Chart.">
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder="Select document types"
            value={form.documentTypeIds}
            onChange={(documentTypeIds) => update({ documentTypeIds })}
            options={documentTypeOptions}
          />
        </Form.Item>
        <Space style={{ display: 'flex' }} align="start">
          <Form.Item label="Task category" extra="Optional. Staff filter NextGen tasks by category." style={{ width: 300 }}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select a category"
              value={form.categoryId}
              onChange={(categoryId, option) => update({ categoryId, categoryLabel: option?.label })}
              options={withSaved(options.categories, form.categoryId, form.categoryLabel)}
            />
          </Form.Item>
          <Form.Item label="Priority" required style={{ width: 150 }}>
            <Select
              placeholder="Priority"
              value={form.priority}
              onChange={(priority, option) => update({ priority, priorityLabel: option.label })}
              options={withSaved(options.priorities, form.priority, form.priorityLabel)}
            />
          </Form.Item>
          <Form.Item label="Due in (days)" style={{ width: 120 }}>
            <InputNumber min={0} max={365} value={form.dueInDays} onChange={(dueInDays) => update({ dueInDays })} />
          </Form.Item>
        </Space>
        <Form.Item
          label="Subject"
          extra='Sent as "Medsender: <subject>". Left blank, it uses the document title, then the document type.'
        >
          <Input
            aria-label="Subject"
            value={form.subject}
            maxLength={SUBJECT_MAX_LENGTH}
            showCount
            onChange={(e) => update({ subject: e.target.value })}
          />
        </Form.Item>
        <Form.Item label="Fields to fill in" extra="Each answer is added to the task description.">
          {form.fields.map((field, index) => (
            <Space key={field.uid} align="start" style={{ display: 'flex', marginBottom: 8 }}>
              <Input
                aria-label={`Field ${index + 1} label`}
                placeholder="Label"
                value={field.label}
                maxLength={100}
                style={{ width: 150 }}
                onChange={(e) => updateField(index, { label: e.target.value })}
              />
              <Select
                aria-label={`Field ${index + 1} type`}
                value={field.type}
                style={{ width: 180 }}
                options={FIELD_TYPES}
                onChange={(type) => updateField(index, { type })}
              />
              {field.type === 'select' && (
                <Select
                  aria-label={`Field ${index + 1} options`}
                  mode="tags"
                  placeholder="Options"
                  tokenSeparators={[',']}
                  value={field.options || []}
                  style={{ width: 160 }}
                  onChange={(choices) => updateField(index, { options: choices })}
                />
              )}
              <Checkbox checked={!!field.required} onChange={(e) => updateField(index, { required: e.target.checked })}>
                Required
              </Checkbox>
              <Button
                aria-label={`Remove field ${index + 1}`}
                icon={<DeleteOutlined />}
                onClick={() => update({ fields: form.fields.filter((_, position) => position !== index) })}
              />
            </Space>
          ))}
          <Button
            icon={<PlusOutlined />}
            onClick={() => {
              const uid = nextUidRef.current;
              nextUidRef.current += 1;
              update({
                fields: [...form.fields, {
                  label: '', type: 'text', required: false, uid,
                }],
              });
            }}
          >
            Add field
          </Button>
        </Form.Item>
      </Form>
    </ModalWrapper>
  );
};

AddNextgenTaskTemplate.defaultProps = {
  template: {},
};

AddNextgenTaskTemplate.propTypes = {
  credentialId: PropTypes.number.isRequired,
  ownerOptions: PropTypes.arrayOf(PropTypes.shape({
    owner_type: PropTypes.string,
    owner_id: PropTypes.number,
    label: PropTypes.string,
  })).isRequired,
  template: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default AddNextgenTaskTemplate;
